import React, { useState, useEffect } from 'react';
import CalcShell from '../CalcShell';
import { CNetRow, CNetActions, CNetResult, G } from './_cnet';

// Force units → newton
const F_UNITS = { newton: 1, 'kilogram-force': 9.80665, 'pound-force': 4.44822, dyne: 1e-5 };
// Distance units → meter
const D_UNITS = { meter: 1, kilometer: 1000, foot: 0.3048, mile: 1609.344, inch: 0.0254 };
// Time units → seconds
const T_UNITS = { second: 1, minute: 60, hour: 3600 };
// Power units → watt
const HP_UNITS = {
  'Watt': 1,
  'Kilowatt': 1000,
  'Mechanical Horsepower': 745.699872,
  'Metric Horsepower': 735.49875,
  'Electrical Horsepower': 746,
  'Boiler Horsepower': 9809.5,
  'BTU/hour': 0.29307107,
  'Foot-pound/second': 1.35582,
};

export default function HorsepowerCalc() {
  return (
    <CalcShell look="cnet" showActions={false} title="Horsepower Calculator" subtitle="Compute horsepower according to its definition (Power = Force × distance ÷ time) and convert between all common power units.">
      <p className="text-sm text-slate-700 mb-4">Horsepower is a measurement of power, or the rate at which work is done. This calculator can be used to compute horsepower according to its definition and to convert between different power units. To find the engine horsepower of a vehicle, please use our <a href="/calc/engine-horsepower" className="text-blue-600 underline">Engine Horsepower Calculator</a>.</p>
      <DefinitionSection />
      <ConverterSection />
    </CalcShell>
  );
}

function DefinitionSection() {
  const [force, setForce] = useState(10);
  const [fU, setFU] = useState('newton');
  const [dist, setDist] = useState(3);
  const [dU, setDU] = useState('meter');
  const [time, setTime] = useState(2);
  const [tU, setTU] = useState('second');
  const [res, setRes] = useState(null);
  const calc = () => {
    const F = Number(force) * F_UNITS[fU];
    const D = Number(dist) * D_UNITS[dU];
    const T = Number(time) * T_UNITS[tU];
    if (!T) { setRes({ error: 'Time cannot be zero.' }); return; }
    const watts = (F * D) / T;
    setRes({ watts });
  };
  useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [force, fU, dist, dU, time, tU]);
  return (
    <>
      <h3 className="cnet-section-title">Horsepower Calculation Based on Definition</h3>
      <p className="text-sm text-slate-700 mb-2">By definition, Power = Force × (distance ÷ time).</p>
      <div className="cnet-panel">
        <CNetRow label="Force">
          <input data-testid="hp-force" className="cnet-input" type="number" value={force} onChange={e => setForce(e.target.value)} />
          <select className="cnet-select" value={fU} onChange={e => setFU(e.target.value)}>{Object.keys(F_UNITS).map(k => <option key={k}>{k}</option>)}</select>
        </CNetRow>
        <CNetRow label="Distance">
          <input data-testid="hp-dist" className="cnet-input" type="number" value={dist} onChange={e => setDist(e.target.value)} />
          <select className="cnet-select" value={dU} onChange={e => setDU(e.target.value)}>{Object.keys(D_UNITS).map(k => <option key={k}>{k}</option>)}</select>
        </CNetRow>
        <CNetRow label="Time">
          <input data-testid="hp-time" className="cnet-input" type="number" value={time} onChange={e => setTime(e.target.value)} />
          <select className="cnet-select" value={tU} onChange={e => setTU(e.target.value)}>{Object.keys(T_UNITS).map(k => <option key={k}>{k}</option>)}</select>
        </CNetRow>
        <CNetActions onCalc={calc} onClear={() => setRes(null)} testId="hp-def" />
      </div>
      {res && res.error && <div className="text-red-600 mt-2">{res.error}</div>}
      {res && !res.error && (
        <CNetResult>
          <div className="text-xl font-semibold text-slate-900" data-testid="hp-def-result">
            Power = <span className="text-[#4c7b25]">{G(res.watts / 745.699872, 4)}</span> mechanical hp
          </div>
          <table className="cnet-info-table mt-3">
            <tbody>
              <tr><td>Watts</td><td>{G(res.watts, 4)} W</td></tr>
              <tr><td>Kilowatts</td><td>{G(res.watts / 1000, 6)} kW</td></tr>
              <tr><td>Metric horsepower</td><td>{G(res.watts / 735.49875, 4)} PS</td></tr>
              <tr><td>Electrical horsepower</td><td>{G(res.watts / 746, 4)} hp(E)</td></tr>
            </tbody>
          </table>
        </CNetResult>
      )}
    </>
  );
}

function ConverterSection() {
  const [amount, setAmount] = useState(1);
  const [fromU, setFromU] = useState('Mechanical Horsepower');
  const [toU, setToU] = useState('Watt');
  const [res, setRes] = useState(null);
  const calc = () => {
    const watts = Number(amount) * HP_UNITS[fromU];
    setRes({ watts, converted: watts / HP_UNITS[toU] });
  };
  useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [amount, fromU, toU]);
  return (
    <>
      <h3 className="cnet-section-title mt-6">Horsepower Converter</h3>
      <p className="text-sm text-slate-700 mb-2">Converts between different units of power.</p>
      <div className="cnet-panel">
        <CNetRow label="Amount">
          <input data-testid="hp-conv-amount" className="cnet-input" type="number" step="any" value={amount} onChange={e => setAmount(e.target.value)} />
          <span />
        </CNetRow>
        <CNetRow label="Convert From">
          <select data-testid="hp-conv-from" className="cnet-select" value={fromU} onChange={e => setFromU(e.target.value)}>{Object.keys(HP_UNITS).map(u => <option key={u}>{u}</option>)}</select>
          <span />
        </CNetRow>
        <CNetRow label="Convert To">
          <select data-testid="hp-conv-to" className="cnet-select" value={toU} onChange={e => setToU(e.target.value)}>{Object.keys(HP_UNITS).map(u => <option key={u}>{u}</option>)}</select>
          <span />
        </CNetRow>
        <CNetActions onCalc={calc} onClear={() => setRes(null)} testId="hp-conv" />
      </div>
      {res && (
        <CNetResult>
          <div className="text-xl font-semibold text-slate-900" data-testid="hp-conv-result">
            {G(amount, 6)} {fromU} = <span className="text-[#4c7b25]">{G(res.converted, 6)}</span> {toU}
          </div>
          <table className="cnet-info-table mt-3">
            <tbody>
              {Object.entries(HP_UNITS).map(([u, k]) => (
                <tr key={u} className={u === toU ? 'bg-emerald-50' : ''}><td>{u}</td><td>{G(res.watts / k, 6)}</td></tr>
              ))}
            </tbody>
          </table>
        </CNetResult>
      )}
    </>
  );
}
